import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { DrumPortrait } from "@/components/DrumPortrait";
import { Reveal } from "@/components/Reveal";
import { formatCHF } from "@/lib/api";
import { useNoIndex } from "@/lib/seo";
import { useSeo } from "@/lib/seo";

const Cart = () => {
  useSeo({ title: "Panier · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const { items, removeItem, total } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Panier — Entre les Mondes";
  }, []);

  return (
    <div className="pt-40 pb-24">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal>
          <p className="eyebrow mb-4">Votre panier</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-bark mb-12" data-testid="cart-title">
            {items.length === 0 ? "Le panier est encore silencieux." : `${items.length} tambour${items.length > 1 ? "s" : ""} vous attend${items.length > 1 ? "ent" : ""}.`}
          </h1>
        </Reveal>

        {items.length === 0 ? (
          <Reveal delay={0.1}>
            <Link to="/boutique" data-testid="cart-empty-cta"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300">
              Écouter les tambours <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        ) : (
          <div className="space-y-6">
            {items.map((item) => (
              <Reveal key={item.id}>
                <div className="flex items-center gap-6 bg-card border border-sand-dark rounded-2xl p-5" data-testid={`cart-item-${item.numero.replace(/\s/g, "-").toLowerCase()}`}>
                  {item.image ? (
                    <img src={item.image} alt={item.nom} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                  ) : (
                    <div className="w-20 h-20 bg-earth rounded-xl p-3 shrink-0">
                      <DrumPortrait peau={item.couleur_peau} bois={item.couleur_bois} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Link to={`/boutique/tambour/${item.id}`} className="font-serif text-xl text-bark hover:text-terra transition-colors">
                      {item.nom}
                    </Link>
                    <p className="text-sm text-fumee mt-1">{[item.numero, item.peau, item.forme, item.diametre ? `${item.diametre} cm` : null].filter(Boolean).join(" · ")}{item.forme === "Accessoire" ? "" : " · pièce unique"}</p>
                  </div>
                  <span className="font-serif text-xl text-terra shrink-0">{formatCHF(item.prix)}</span>
                  <button onClick={() => removeItem(item.id)} data-testid={`cart-remove-${item.numero.replace(/\s/g, "-").toLowerCase()}`}
                    aria-label={`Retirer ${item.nom}`}
                    className="p-2.5 rounded-full border border-wood/20 text-fumee hover:border-destructive hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.1}>
              <div className="bg-sand-dark/50 border border-sand-dark rounded-2xl p-8 mt-10">
                <div className="flex justify-between text-sm text-fumee mb-3">
                  <span>Livraison Suisse</span><span className="text-sage">Offerte</span>
                </div>
                <div className="flex justify-between items-baseline pt-4 border-t border-sand-dark">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee">Total</span>
                  <span className="font-serif text-3xl text-bark" data-testid="cart-total">{formatCHF(total)}</span>
                </div>
                <button onClick={() => navigate("/checkout")} data-testid="cart-checkout"
                  className="mt-8 w-full py-4 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300">
                  Passer au paiement (simulé)
                </button>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
